// The creator timeline as a pure state machine plus a parser for the paged
// response, so CreatorTimeline.tsx only wires them to the network.

import { CreatorPostsPage, SignedMedia, TimelinePost } from '../types/post';
import { readMediaKind } from './postMedia';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function readCreatedAt(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Drops any entry without a usable URL, so no card ever renders an empty `src`. */
function parseMedia(value: unknown): SignedMedia[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const media: SignedMedia[] = [];
  value.forEach(entry => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const { url, contentType, kind, expiresAt } = entry as Record<string, unknown>;
    if (!isNonEmptyString(url)) {
      return;
    }
    media.push({
      url,
      contentType: typeof contentType === 'string' ? contentType : '',
      // The backend always sends `kind`, but a row that doesn't is classified
      // the same way the backend would classify it.
      kind: readMediaKind({ kind, contentType }),
      expiresAt: isNonEmptyString(expiresAt) ? expiresAt : null,
    });
  });
  return media;
}

/**
 * Reads one row. Fails closed the same way the backend's `toUnlockedPost` does:
 * only an explicit `locked: false` opens a post up, so a shape we don't
 * recognise is shown as locked rather than as a post with a missing image.
 */
function parsePost(value: unknown): TimelinePost | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const row = value as Record<string, unknown>;
  if (!isNonEmptyString(row.id)) {
    return null;
  }

  const createdAt = readCreatedAt(row.createdAt);

  if (row.locked === false) {
    return {
      id: row.id,
      creatorId: typeof row.creatorId === 'string' ? row.creatorId : null,
      caption: typeof row.caption === 'string' ? row.caption : '',
      gated: row.gated !== false,
      createdAt,
      locked: false,
      media: parseMedia(row.media),
    };
  }

  const count = row.mediaCount;
  return {
    id: row.id,
    createdAt,
    locked: true,
    mediaCount: typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0,
  };
}

/** Reads one page of `GET /api/posts/creator/:creatorId`. */
export function parsePostsPage(data: unknown): CreatorPostsPage {
  const empty: CreatorPostsPage = { posts: [], nextCursor: null, hasMore: false };

  if (!data || typeof data !== 'object') {
    return empty;
  }

  const body = data as Record<string, unknown>;
  const rows = Array.isArray(body.posts) ? body.posts : [];

  const posts: TimelinePost[] = [];
  rows.forEach(row => {
    const parsed = parsePost(row);
    if (parsed) {
      posts.push(parsed);
    }
  });

  const nextCursor = isNonEmptyString(body.nextCursor) ? body.nextCursor : null;

  return {
    posts,
    nextCursor,
    // A cursor we cannot use is the same as no more pages: never promise a page
    // the next request could not ask for.
    hasMore: body.hasMore === true && nextCursor !== null,
  };
}

export type TimelineStatus = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error';

export interface TimelineState {
  posts: TimelinePost[];
  cursor: string | null;
  hasMore: boolean;
  status: TimelineStatus;
  error: string | null;
}

export const initialTimelineState: TimelineState = {
  posts: [],
  cursor: null,
  hasMore: false,
  status: 'idle',
  error: null,
};

export type TimelineEvent =
  | { type: 'loadFirst' }
  | { type: 'loadMore' }
  | { type: 'retry' }
  | { type: 'pageLoaded'; page: CreatorPostsPage; append: boolean }
  | { type: 'postsRefreshed'; page: CreatorPostsPage }
  | { type: 'fail'; message: string }
  | { type: 'reset' };

/** True while a request is in flight, for the spinner and to disable Retry. */
export function isTimelineBusy(state: TimelineState): boolean {
  return state.status === 'loading' || state.status === 'loadingMore';
}

/** "No posts yet" belongs on screen only once a first page came back empty. */
export function timelineIsEmpty(state: TimelineState): boolean {
  return state.status === 'ready' && state.posts.length === 0;
}

/**
 * Whether a fresh page 1 may be requested. The component checks this before it
 * fires the request, so a refresh cannot overtake a load already in flight.
 */
export function canLoadFirst(state: TimelineState): boolean {
  return !isTimelineBusy(state);
}

/**
 * Whether the next page may be requested. The IntersectionObserver fires on
 * every scroll nudge; this is what keeps that to one request per cursor.
 */
export function canLoadMore(state: TimelineState): boolean {
  return state.status === 'ready' && state.hasMore && state.cursor !== null;
}

/**
 * Which request Retry should repeat: the page that failed. Posts already on
 * screen mean it was a later page, so retrying resumes from the stored cursor
 * and appends; otherwise it is page 1 again and replaces.
 */
export function timelineRetryTarget(
  state: TimelineState,
): { cursor: string | null; append: boolean } {
  if (state.posts.length > 0 && state.cursor !== null) {
    return { cursor: state.cursor, append: true };
  }
  return { cursor: null, append: false };
}

/** Appends a page, dropping ids already on screen. */
function mergePosts(existing: TimelinePost[], incoming: TimelinePost[]): TimelinePost[] {
  // A post published between two page requests shifts the window, so the same
  // id can come back on the next page. React keys must stay unique.
  const seen = new Set(existing.map(post => post.id));
  const merged = existing.slice();

  incoming.forEach(post => {
    if (seen.has(post.id)) {
      return;
    }
    seen.add(post.id);
    merged.push(post);
  });

  return merged;
}

/**
 * Events that do not fit the current state are ignored (the same object is
 * returned). That guard is what stops a repeatedly firing IntersectionObserver
 * from launching overlapping requests for the same cursor.
 */
export function timelineReducer(
  state: TimelineState,
  event: TimelineEvent,
): TimelineState {
  switch (event.type) {
    case 'loadFirst':
      if (!canLoadFirst(state)) {
        return state;
      }
      // The posts stay on screen until the new page arrives, so a refresh after
      // subscribing does not blank the timeline.
      return { ...state, status: 'loading', error: null };

    case 'loadMore':
      if (!canLoadMore(state)) {
        return state;
      }
      return { ...state, status: 'loadingMore', error: null };

    case 'retry':
      if (state.status !== 'error') {
        return state;
      }
      return {
        ...state,
        status: timelineRetryTarget(state).append ? 'loadingMore' : 'loading',
        error: null,
      };

    case 'pageLoaded': {
      const posts = event.append
        ? mergePosts(state.posts, event.page.posts)
        : event.page.posts;

      return {
        posts,
        cursor: event.page.nextCursor,
        hasMore: event.page.hasMore,
        status: 'ready',
        error: null,
      };
    }

    case 'postsRefreshed': {
      // Freshly signed copies of posts already on screen, swapped in place.
      // `cursor`, `hasMore`, `status` and `error` are deliberately untouched:
      // re-reading page 1 must never truncate a list that has paged past it,
      // and a post page 1 no longer returns keeps the copy it already had.
      const fresh = new Map<string, TimelinePost>();
      event.page.posts.forEach(post => fresh.set(post.id, post));
      if (fresh.size === 0) {
        return state;
      }
      return { ...state, posts: state.posts.map(post => fresh.get(post.id) ?? post) };
    }

    case 'fail':
      // The cursor and the posts survive, so Retry resumes where paging stopped
      // instead of emptying the list.
      return { ...state, status: 'error', error: event.message };

    case 'reset':
      return initialTimelineState;

    default:
      return state;
  }
}
